import React from 'react';
import { Container, Card } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import '../styles/pages/TermsOfService.css';

const TermsOfService = () => {
  return (
    <Container className="terms-of-service-container">
      <Helmet>
        <title>Terms of Service - WeiFinders</title>
        <meta
          name="description"
          content="Read the Terms of Service for WeiFinders, detailing user responsibilities, subscriptions, content guidelines, data practices, and third-party integrations."
        />
        <meta property="og:title" content="Terms of Service - WeiFinders" />
        <meta
          property="og:description"
          content="Read the Terms of Service for WeiFinders, detailing user responsibilities, subscriptions, content guidelines, data practices, and third-party integrations."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://weifinders.com/terms-of-service" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta name="twitter:title" content="Terms of Service - WeiFinders" />
        <meta
          name="twitter:description"
          content="Read the Terms of Service for WeiFinders, detailing user responsibilities, subscriptions, content guidelines, data practices, and third-party integrations."
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Terms of Service - WeiFinders",
            "url": "https://weifinders.com/terms-of-service",
            "description": "Terms of Service outlining user responsibilities, account management, subscriptions, data practices, and more."
          }
        `}</script>
      </Helmet>

      <Card className="terms-of-service-card">
        <Card.Body>
          <article role="article">
            <h1>Terms of Service</h1>
            <p><strong>Last Updated:</strong> 13th May 2025</p>

            {/* 1. Introduction */}
            <section>
              <h2>1. Introduction</h2>
              <p>
                Welcome to WeiFinders. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms"). If you do not agree with these Terms, please do not use our services.
              </p>
            </section>

            {/* 2. Eligibility */}
            <section>
              <h2>2. Eligibility</h2>
              <p>
                You must be 18 years of age or older to use WeiFinders. By using our platform, you represent and warrant that you meet this age requirement.
              </p>
            </section>

            {/* 3. Account Registration */}
            <section>
              <h2>3. Account Registration</h2>
              <p>
                To create content such as guides, itineraries, or places, you must register an account via email signup or Google Login. You are responsible for maintaining the confidentiality of your credentials and for all activities under your account.
              </p>
            </section>

            {/* 4. User Content */}
            <section>
              <h2>4. User Content</h2>
              <p>
                You retain ownership of any content you create and share on WeiFinders, including blogs, guides, itineraries, images, and embedded media. By posting any content on our platform, you grant WeiFinders a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, and distribute that content for operating and promoting our services.
              </p>
              <ul>
                <li>
                  <strong>Chatbot &amp; FAQ Content:</strong>
                  Any dialog you have with our chatbot and any question/answer you opt to publish in our public FAQ is treated as User Content under these Terms.
                </li>
                <li>
                  <strong>Removal:</strong>
                  Retention, anonymization, and deletion details are governed by our Privacy Policy (see Section 6 “Data Retention and Breach Notification”).
                </li>
              </ul>
            </section>

            {/* 5. Subscription & Billing */}
            <section>
              <h2>5. Subscription &amp; Billing</h2>
              <p>
                WeiFinders offers subscription-based services with recurring payments via Stripe. We do not store sensitive payment information. By subscribing, you agree to the terms of recurring charges. Cancellation is available at any time; refunds as required by law. Fees may be modified with advance notice.
              </p>
            </section>

            {/* 6. Acceptable Use */}
            <section>
              <h2>6. Acceptable Use</h2>
              <p>
                You agree to use WeiFinders in compliance with all laws and not to interfere with the functioning of our platform or other users’ experience.
              </p>
            </section>

            {/* 7. Intellectual Property */}
            <section>
              <h2>7. Intellectual Property</h2>
              <p>
                All trademarks and content on WeiFinders are property of WeiFinders or its licensors. You may not use our IP without written consent, except for personal, non-commercial use.
              </p>
            </section>

            {/* 8. Third-Party Integrations */}
            <section>
              <h2>8. Third-Party Integrations</h2>
              <p>
                WeiFinders integrates third-party services to enhance functionality. This includes:
              </p>
              <ul>
                <li>
                  <strong>Google Services:</strong> By using our platform, you agree to the Google Maps, Google Places, and related Google API terms. Please refer to:
                  <ul>
                    <li>
                      <a href="https://cloud.google.com/maps-platform/terms" target="_blank" rel="noopener noreferrer">
                        Google Maps Platform Terms of Service
                      </a>
                    </li>
                    <li>
                      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                        Google Terms of Service
                      </a>
                    </li>
                    <li>
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                        Google Privacy Policy
                      </a>
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>GeoNames:</strong> Our platform uses geographical data from <a href="http://www.geonames.org" target="_blank" rel="noopener noreferrer">GeoNames</a>, licensed under the <a href="http://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">Creative Commons Attribution 4.0 License</a>.
                </li>
              </ul>
            </section>

            {/* 9. Termination */}
            <section>
              <h2>9. Termination</h2>
              <p>
                We reserve the right to suspend or terminate access without notice if you violate these Terms or engage in harmful conduct.
              </p>
            </section>

            {/* 10. Liability and Disclaimers */}
            <section>
              <h2>10. Liability and Disclaimers</h2>
              <p>
                Our platform is provided "as is" without warranties. We are not liable for indirect or consequential damages.
              </p>
            </section>

            {/* 11. Indemnification */}
            <section>
              <h2>11. Indemnification</h2>
              <p>
                You agree to indemnify and hold WeiFinders harmless from any claims arising from your use of the platform or violation of these Terms.
              </p>
            </section>

            {/* 12. Governing Law */}
            <section>
              <h2>12. Governing Law</h2>
              <p>
                These Terms are governed by the laws of England and Wales, without regard to conflict of law principles.
              </p>
            </section>

            {/* 13. Dispute Resolution */}
            <section>
              <h2>13. Dispute Resolution</h2>
              <p>
                Any disputes will be resolved through binding arbitration under the jurisdiction of England and Wales.
              </p>
            </section>

            {/* 14. Updates to Terms */}
            <section>
              <h2>14. Updates to Terms</h2>
              <p>
                We may update these Terms periodically. Continued use constitutes acceptance of revised Terms.
              </p>
            </section>

            {/* 15. Contact Information */}
            <section>
              <h2>15. Contact Information</h2>
              <p>Questions? Contact us at:</p>
              <ul>
                <li><strong>Email:</strong> <a href="mailto:support@weifinders.com">support@weifinders.com</a></li>
                <li><strong>Address:</strong> WeiFinders, Gemma House, 39 Lilestone Street, London, England, NW8 8SS</li>
                <li><strong>Contact Page:</strong> <a href="/contact" rel="noopener noreferrer">Contact Us</a></li>
              </ul>
            </section>

            {/* 16. Data Retention */}
            <section>
              <h2>16. Data Retention</h2>
              <p>
                Retention and anonymization of your data are governed by our Privacy Policy (see Section 6 “Data Retention and Breach Notification”).
              </p>
            </section>

            {/* 17. Severability and Entire Agreement */}
            <section>
              <h2>17. Severability and Entire Agreement</h2>
              <p>
                If any provision is invalid, the remainder remain in effect. These Terms, along with our Privacy Policy, constitute the entire agreement.
              </p>
            </section>

            {/* 18. No Waiver */}
            <section>
              <h2>18. No Waiver</h2>
              <p>
                Failure to enforce any provision is not a waiver of future enforcement rights.
              </p>
            </section>

            {/* 19. Third-Party Links and External Content */}
            <section>
              <h2>19. Third-Party Links and External Content</h2>
              <p>
                We are not responsible for third-party sites or content accessed via links on our platform.
              </p>
            </section>

            {/* 20. Content Moderation and Prohibited Content */}
            <section>
              <h2>20. Content Moderation and Prohibited Content</h2>
              <p>
                We may monitor and remove user-generated content that violates these Terms or applicable laws.
              </p>
            </section>

            {/* 21. Preferences & Support Interactions */}
            <section>
              <h2>21. Preferences &amp; Support Interactions</h2>
              <p>
                <strong>Preferences:</strong> Your saved destinations, filters, and personalization settings are used to tailor our services. Upon account deletion, preferences are anonymized and aggregated for product improvement.
              </p>
              <p>
                <p><strong>Support & Feedback:</strong> Messages via support channels are retained while your account exists, then stripped of all direct identifiers within 30 days of account deletion; anonymized feedback may be kept indefinitely for product improvement.</p>
              </p>
            </section>

          </article>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TermsOfService;
